import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Container,
  Alert,
  Paper,
  CircularProgress,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  Divider
} from '@mui/material';
import {
  Lock as LockIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Security as SecurityIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import PasswordField from '../shared/PasswordField';
import { useAuth } from '../../contexts/AuthContext';
import SimpleNavbar from '../shared/SimpleNavbar';

const ChangePassword = () => {
  const theme = useTheme();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { user, loading, updateUser } = useAuth();

  // Password strength calculation
  const getPasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength += 25;
    if (/[a-z]/.test(password)) strength += 25;
    if (/[A-Z]/.test(password)) strength += 25;
    if (/[0-9]/.test(password)) strength += 12.5;
    if (/[^a-zA-Z0-9]/.test(password)) strength += 12.5;
    return strength;
  };

  const passwordStrength = getPasswordStrength(newPassword);

  const getStrengthColor = (strength) => {
    if (strength < 50) return theme.palette.error.main;
    if (strength < 75) return theme.palette.warning.main;
    return theme.palette.success.main;
  };

  const getStrengthLabel = (strength) => {
    if (strength < 50) return 'Weak';
    if (strength < 75) return 'Medium';
    return 'Strong';
  };

  // Password requirements check
  const requirements = [
    { label: 'At least 8 characters', met: newPassword.length >= 8 },
    { label: 'Contains lowercase letter', met: /[a-z]/.test(newPassword) },
    { label: 'Contains uppercase letter', met: /[A-Z]/.test(newPassword) },
    { label: 'Contains a number', met: /[0-9]/.test(newPassword) },
    { label: 'Contains special character', met: /[^a-zA-Z0-9]/.test(newPassword) },
    { label: 'Passwords match', met: newPassword && confirmPassword && newPassword === confirmPassword }
  ];

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login');
    }
  }, [user, loading, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (currentPassword === newPassword) {
      setError('New password must be different from current password');
      return;
    }

    if (passwordStrength < 75) {
      setError('Please choose a stronger password');
      return;
    }

    setIsLoading(true);

    try {
      await api.post('/auth/change-password', {
        currentPassword,
        newPassword
      });

      setSuccess('Password has been changed successfully! Redirecting...');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      updateUser({ isFirstLogin: false });

      setTimeout(() => {
        if (user && user.termsAccepted === false) {
          navigate('/terms-acceptance');
        } else if (user && user.isSuperAdmin) {
          navigate('/super-admin');
        } else {
          navigate('/app/dashboard');
        }
      }, 2000);
    } catch (err) {
      console.error('Change password error:', err);
      setError(err.response?.data?.message || 'Failed to change password');
    } finally {
      setIsLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
        <SimpleNavbar />
        <Container component="main" maxWidth="xs">
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 'calc(100vh - 64px)' }}>
            <CircularProgress />
          </Box>
        </Container>
      </Box>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: theme.palette.grey[50] }}>
      <SimpleNavbar />
      <Container component="main" maxWidth="sm">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            py: 6
          }}
        >
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, sm: 5 },
              width: '100%',
              border: 1,
              borderColor: theme.palette.divider,
              borderRadius: 3
            }}
          >
            {/* Header */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  bgcolor: theme.palette.primary.main + '15',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2
                }}
              >
                <LockIcon sx={{ fontSize: 32, color: theme.palette.primary.main }} />
              </Box>
              <Typography
                component="h1"
                variant="h4"
                sx={{ fontWeight: 700, color: theme.palette.text.primary, mb: 1 }}
              >
                Change Password
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {user?.isFirstLogin
                  ? 'For security, please change your password before continuing.'
                  : 'Create a strong password to secure your account.'
                }
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
                {success}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit}>
              <PasswordField
                margin="normal"
                required
                fullWidth
                name="currentPassword"
                label="Current Password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                disabled={isLoading}
                sx={{ mb: 2 }}
              />

              <Divider sx={{ my: 3 }}>
                <Typography variant="caption" color="text.secondary" sx={{ px: 2 }}>
                  New Password
                </Typography>
              </Divider>

              <PasswordField
                margin="normal"
                required
                fullWidth
                name="newPassword"
                label="New Password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={isLoading}
                sx={{ mb: 1 }}
              />

              {/* Password Strength Indicator */}
              {newPassword && (
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      Password Strength
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        color: getStrengthColor(passwordStrength)
                      }}
                    >
                      {getStrengthLabel(passwordStrength)}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={passwordStrength}
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      bgcolor: theme.palette.grey[200],
                      '& .MuiLinearProgress-bar': {
                        bgcolor: getStrengthColor(passwordStrength),
                        borderRadius: 3
                      }
                    }}
                  />
                </Box>
              )}

              <PasswordField
                margin="normal"
                required
                fullWidth
                name="confirmPassword"
                label="Confirm New Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={isLoading}
                error={confirmPassword && newPassword !== confirmPassword}
                helperText={confirmPassword && newPassword !== confirmPassword ? 'Passwords do not match' : ''}
                sx={{ mb: 2 }}
              />

              {/* Password Requirements */}
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  bgcolor: theme.palette.grey[50],
                  borderRadius: 2,
                  mb: 3
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                  <SecurityIcon sx={{ fontSize: 18, color: theme.palette.text.secondary, mr: 1 }} />
                  <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Password Requirements
                  </Typography>
                </Box>
                <List dense disablePadding>
                  {requirements.map((req, index) => (
                    <ListItem key={index} disablePadding sx={{ py: 0.25 }}>
                      <ListItemIcon sx={{ minWidth: 28 }}>
                        {req.met ? (
                          <CheckIcon sx={{ fontSize: 18, color: theme.palette.success.main }} />
                        ) : (
                          <CloseIcon sx={{ fontSize: 18, color: theme.palette.grey[400] }} />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={req.label}
                        primaryTypographyProps={{
                          variant: 'body2',
                          color: req.met ? 'text.primary' : 'text.secondary',
                          sx: { fontWeight: req.met ? 500 : 400 }
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={isLoading || passwordStrength < 75 || newPassword !== confirmPassword}
                startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <LockIcon />}
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '1rem'
                }}
              >
                {isLoading ? 'Changing Password...' : 'Change Password'}
              </Button>

              {!user?.isFirstLogin && (
                <Button
                  fullWidth
                  variant="text"
                  onClick={() => navigate(-1)}
                  sx={{
                    mt: 2,
                    textTransform: 'none',
                    color: 'text.secondary'
                  }}
                >
                  Cancel
                </Button>
              )}
            </Box>
          </Paper>
        </Box>
      </Container>
    </Box>
  );
};

export default ChangePassword;
